/**
 * Composable to manage available locales per region for specific pages
 *
 * This composable centralizes the configuration of which locales are available
 * for each page in each region. It provides validation and returns the allowed
 * locales for the current region.
 *
 * @example
 * // In tickets.vue
 * const { allowedLocales, isLocaleAllowed } = usePageLocalesByRegion('tickets');
 *
 * // Block access if current locale is not allowed
 * if (!isLocaleAllowed($getLocale())) {
 *   throw createError({ statusCode: 404 });
 * }
 *
 * // Configure page meta tags
 * if (allowedLocales) {
 *   definePageLocales(allowedLocales);
 * }
 */

interface UsePageLocalesByRegion {
  allowedLocales: string[] | null;
  isLocaleAllowed: (locale: string) => boolean;
}

export const usePageLocalesByRegion = (pageName: string): UsePageLocalesByRegion => {
  const route = useRoute();
  const region = route.params.region as string;

  // Configuration: which locales are available for each page in each region
  const pageRegionLocales: Record<string, Record<string, string[]>> = {
    tickets: {
      espana: ["es", "en"],
      americas: ["es"],
      italia: ["it"],
      brazil: ["pt"],
      germany: ["de"],
      france: ["fr"],
      mena: ["ar", "en"],
    },
    // Add more pages here as needed
    // regulation: { ... },
    // faqs: { ... },
  };

  // Get allowed locales for current page and region
  const allowedLocales = pageRegionLocales[pageName]?.[region] || null;

  /**
   * Check if a specific locale is allowed for the current page and region
   * Returns true if no restriction is set (allowedLocales is null)
   */
  const isLocaleAllowed = (locale: string): boolean => {
    if (!allowedLocales) return true;
    return allowedLocales.includes(locale);
  };

  return {
    allowedLocales,
    isLocaleAllowed,
  };
};
